import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebaseApp';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';

export const syncData = async () => {
  const response = await httpsCallable(functions, 'syncDataV2')();

  return response.data;
};

export const getLastSyncDate = async () => {
  try {
    const syncDataRef = collection(db, 'syncData');

    const q = query(syncDataRef, orderBy('lastSyncAt', 'desc'), limit(1));

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const earliestDocument = querySnapshot.docs[0].data();
      return earliestDocument;
    }
    return null;
  } catch (error: any) {
    toast.error('Error Retrieving Last Sync Date!');
  }
};

export const syncCSVClientData = async () => {
  try {
    const response: any = await httpsCallable(functions, 'generateAndSyncClientDataV2')();
    if (response.data.success) {
      toast.success('Client Data CSV Uploaded Successfully!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};

export const syncUnemployedParticipants = async () => {
  try {
    const response: any = await httpsCallable(functions, 'exportUnemployedUnenrolledParticipantsV2')();
    if (response.data.success) {
      toast.success('Unemployed Client Data CSV Uploaded Successfully!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};

export const syncClientsRemainingData = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientsRemainingDataV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While syncing client remaining data!');
    }
  } catch (error) {
    toast.error('Error While syncing client remaining data!');
  }
};

export const updateExistingClientsInES = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateExistingClientsInElasticsearchV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Existing Cliets Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Updating Existing Cliets Elasticsearch!');
  }
};

export const exportClientsFromElasticsearch = async (format: string = 'json') => {
  try {
    const response: any = await httpsCallable(functions, 'exportClientsFromElasticsearchV2')({ format });
    if (response.data.success) {
      toast.success(response.data.message);

      // Create and download the file locally
      const { filename, fileContent, contentType } = response.data.data;

      // Create a blob from the file content
      const blob = new Blob([fileContent], { type: contentType });

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response.data;
    } else {
      toast.error('Error While Exporting Clients from Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Exporting Clients from Elasticsearch!');
  }
};

export const exportAllTestIndicesFromElasticsearch = async (format: string = 'json') => {
  try {
    const response: any = await httpsCallable(functions, 'exportAllTestIndicesFromElasticsearchV2')({ format });
    if (response.data.success) {
      toast.success(response.data.message);
      // Create and download the file locally
      const { filename, fileContent, contentType } = response.data.data;

      // Create a blob from the file content
      const blob = new Blob([fileContent], { type: contentType });

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response.data;
    } else {
      toast.error('Error While Exporting All Test Indices from Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Exporting All Test Indices from Elasticsearch!');
  }
};

export const syncClientDataToClientList = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientDataToClientListCollectionV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Creating Client List Data!');
    }
  } catch (error) {
    toast.error('Error While Creating Client List Data!');
  }
};
export const addDummyEntriesInClientList = async () => {
  try {
    const response: any = await httpsCallable(functions, 'addDummyClientsAndIndexToElasticsearchV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Failed to add dummy entries!');
    }
  } catch (error) {
    toast.error('Failed to add dummy entries!');
  }
};

export const deleteDummyEntriesFromClientList = async () => {
  try {
    const response: any = await httpsCallable(functions, 'deleteDummyEntriesV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Failed to Delete Dummy Entries!');
    }
  } catch (error) {
    toast.error('Failed to Delete Dummy Entries!');
  }
};

export const deleteDummyEntriesFromElastic = async () => {
  try {
    const response: any = await httpsCallable(functions, 'deleteDummyEntriesFromElasticV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Failed to Delete Dummy Entries!');
    }
  } catch (error) {
    toast.error('Failed to Delete Dummy Entries!');
  }
};

export const syncClientName = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientNameWithCSVDataV2')();
    if (response.data.success) {
      toast.success('Client Name Synced Successfully!');
    }
  } catch (error: any) {
    toast.error('Syncing Failed!');
  }
};

export const syncInterestClients = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncInterestedInForClientsV2')();
    if (response.data.success) {
      toast.success('InterestedIn Synced Successfully For Clients!');
    }
  } catch (error: any) {
    toast.error('Syncing Failed!');
  }
};

export const batchUpdateClientActiveStatus = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientsStatusToActiveV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Failed To Update Client Active Status In Batch!');
  }
};

export const updateSuspendedClientsStatus = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateSuspendedClientStatusV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Failed To Update Suspended Client Status!');
  }
};

export const addCreatedDateInStaff = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncCreatedDateInStaffFromAuditLogsV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Failed To Add Created Date In Staff!');
  }
};

export const listClientsWithoutPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'listClientsWithoutPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Without Privacy Consent Client Data CSV Uploaded Successfully!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};
export const listEnrolledClientsWithoutPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'listEnrolledClientsWithoutPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Enrolled and Without Privacy Consent Client Data CSV Uploaded!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};
export const listHiredClientsWithoutPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'listHiredClientsWithoutPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Hired and Without Privacy Consent Client Data CSV Uploaded!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};
export const listCompletedClientWithoutPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'listCompletedClientWithoutPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Completed and Without Privacy Consent Client Data CSV Uploaded!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};
export const clientEmploymentModuleStatusUpdate = async () => {
  try {
    const response: any = await httpsCallable(functions, 'clientEmploymentModuleStatusUpdateV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error(' Error While Syncing EmploymentModuleStatus field!');
  }
};
export const syncClientEmploymentStatusFromAuditLogs = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientEmploymentStatusFromAuditLogsV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error(' Error While Syncing Employment Status field!');
  }
};
export const syncEmploymentModuleStatusWithCsv = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncEmploymentModuleStatusWithCsvV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error(' Error While Syncing Employment Module Status With CSV!');
  }
};
export const getClientsMissingPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'getClientsMissingPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Client Data missing privacy consent CSV Uploaded Successfully!');
    }
  } catch (error: any) {
    toast.error('Client Data CSV Uploading Failed!');
  }
};
export const syncClientsPrivacyConsent = async () => {
  try {
    const response: any = await httpsCallable(functions, 'syncClientsPrivacyConsentV2')();
    if (response.data.success) {
      toast.success('Clients Privacy Consent Synced Successfully!');
    }
  } catch (error: any) {
    toast.error('Error While Syncing Privacy Consent of Clients!');
  }
};
export const formatEmailsToLowercaseInClients = async () => {
  try {
    const response: any = await httpsCallable(functions, 'formatEmailsToLowercaseInClientsV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Error While Syncing Privacy Consent of Clients!');
  }
};
export const formatEmailsToLowercaseInCsvData = async () => {
  try {
    const response: any = await httpsCallable(functions, 'formatEmailsToLowercaseInCsvDataV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Error While Syncing Privacy Consent of Clients!');
  }
};
export const moveClientsInOtherInfoSession = async () => {
  try {
    const response: any = await httpsCallable(functions, 'moveClientsInOtherInfoSessionV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Error While Moving Clients To Other Info Session!');
  }
};
export const resetInfoSessionAttendance = async () => {
  try {
    const response: any = await httpsCallable(functions, 'resetInfoSessionAttendanceV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Error While reseting attendance for info session!');
  }
};
export const updateCreatedAtsFromCreatedAt = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateCreatedAtsFromCreatedAtV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    }
  } catch (error: any) {
    toast.error('Error While syncing createdAt to clientList!');
  }
};

export const updateExistingJobOpportunitiesInES = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateExistingJobInElasticsearchV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Existing Job Opportunities in Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Updating Existing Job Opportunities in Elasticsearch!');
  }
};

export const updateExistingInfoSessionsInES = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateExistingInfoSessionsInESV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Existing Info Sessions in Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Updating Existing Info Sessions in Elasticsearch!');
  }
};

export const updateJobIndexMapping = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateJobIndexMappingV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Job Index Mapping in Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Updating Job Index Mapping in Elasticsearch!');
  }
};

export const updateExistingWorkshopsInES = async () => {
  try {
    const response: any = await httpsCallable(functions, 'updateExistingWorkshopsInESV2')();
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Existing Workshops in Elasticsearch!');
    }
  } catch (error) {
    toast.error('Error While Updating Existing Workshops in Elasticsearch!');
  }
};
